import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle, ChevronDown, User, LogOut, LayoutDashboard, ShieldCheck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabaseClient';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [igcseSubjects, setIgcseSubjects] = useState([]);
  const [aLevelSubjects, setALevelSubjects] = useState([]);
  const [mobileDropdowns, setMobileDropdowns] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  const { user, profile, signOut } = useAuth();

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const { data, error } = await supabase
          .from('subjects')
          .select('name, slug, level')
          .eq('is_active', true)
          .order('name');
        if (error) throw error;
        setIgcseSubjects(data.filter(s => s.level === 'IGCSE'));
        // Deduplicate A Level subjects (AS & A2 share names) — link to AS slug
        const asSubjects = data.filter(s => s.level === 'AS');
        setALevelSubjects(asSubjects);
      } catch (e) {
        console.error('Failed to load nav subjects:', e);
      }
    };
    fetchSubjects();
  }, []);

  const handleLogout = async () => {
    await signOut();
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Logo URLs
  const headerLogo = "/ASPR_ACDMY_16YRS.png";

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    {
      name: 'IGCSE',
      href: '/igcse',
      dropdown: true,
      items: [
        { name: 'All IGCSE Subjects', href: '/igcse' },
        ...igcseSubjects.map(s => ({ name: s.name, href: `/igcse/${s.slug}` }))
      ]
    },
    {
      name: 'A Levels',
      href: '/a-levels',
      dropdown: true,
      items: [
        { name: 'All A Level Subjects', href: '/a-levels' },
        ...aLevelSubjects.map(s => ({ name: s.name, href: `/a-levels/${s.slug}` }))
      ]
    },
    { name: 'Results', href: '/results' },
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100" data-testid="main-header">
      <div className="container-main">
        <div className="flex items-center justify-between h-16 md:h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={headerLogo} 
              alt="Aspire Academy" 
              className="h-12 sm:h-16 md:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              link.dropdown ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger className={`px-4 py-2 text-lg font-bold transition-colors flex items-center gap-1 ${
                    isActive(link.href) ? 'text-aspire-red' : 'text-charcoal hover:text-aspire-red'
                  }`} data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}>
                    {link.name}
                    <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-white border border-gray-200">
                    {link.items.map((item) => (
                      <DropdownMenuItem key={item.href} asChild>
                        <Link to={item.href} className="cursor-pointer">
                          {item.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-4 py-2 text-lg font-bold transition-colors ${
                    isActive(link.href) ? 'text-aspire-red' : 'text-charcoal hover:text-aspire-red'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/94743652899"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 text-base font-semibold transition-colors"
              data-testid="whatsapp-cta"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp</span>
            </a>

            {/* User Menu / Login */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 border border-gray-200 hover:border-aspire-red transition-colors" data-testid="user-menu">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm font-medium">{profile?.name?.split(' ')[0] || user?.email?.split('@')[0]}</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border border-gray-200">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {profile?.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer flex items-center gap-2 text-aspire-red font-bold">
                        <ShieldCheck className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer flex items-center gap-2 text-red-600">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                className="hidden sm:flex items-center gap-2 px-4 py-2 border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-white text-base font-bold uppercase tracking-wider transition-colors"
                data-testid="login-btn"
              >
                Login
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-charcoal"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-100 py-4 max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
            <nav className="flex flex-col">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.dropdown ? (
                    <>
                      <div className="flex items-center">
                        <Link
                          to={link.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex-1 px-4 py-3 text-base font-medium ${
                            isActive(link.href) ? 'text-aspire-red bg-red-50' : 'text-charcoal hover:bg-gray-50'
                          }`}
                        >
                          {link.name}
                        </Link>
                        <button
                          onClick={() => setMobileDropdowns(prev => ({ ...prev, [link.name]: !prev[link.name] }))}
                          className="px-4 py-3 text-gray-500 hover:text-aspire-red transition-colors"
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileDropdowns[link.name] ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      {mobileDropdowns[link.name] && link.items && (
                        <div className="bg-gray-50 border-l-2 border-aspire-red ml-4">
                          {link.items.filter(item => item.href !== link.href).map(item => (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block px-4 py-2.5 text-sm text-gray-600 hover:text-aspire-red hover:bg-gray-100 transition-colors"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-4 py-3 text-base font-medium block ${
                        isActive(link.href) ? 'text-aspire-red bg-red-50' : 'text-charcoal hover:bg-gray-50'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="border-t border-gray-100 mt-2 pt-4 px-4">
                <a
                  href="https://wa.me/94743652899"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-500 text-white py-3 font-medium"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Us
                </a>
                {!user && (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 bg-aspire-red text-white py-3 font-medium mt-2"
                  >
                    Login / Register
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
